/* EditOrgRenderer.js */

"use strict";
const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");

// UI Text Constants
const UI_TEXT = {
    // Section Headers
    ORG_INFO_HEADER: "Organization Information",
    PASSWORD_COMPLEXITY_HEADER: "Local User Password Complexity",
    PASSWORD_COMPLEXITY_SUBHEADER: "Minimum of:",
    DISTRIBUTION_CENTER_HEADER: "Distribution Center (Advanced)",

    // Field Labels
    NAME: "Name",
    DESCRIPTION: "Description",
    PROVIDER: "Provider",
    AUTH_MECHANISM: "User Authentication Mechanism",
    PASSWORD_LENGTH: "Password Length",
    UPPERCASE_CHARS: "Uppercase Characters",
    LOWERCASE_CHARS: "Lowercase Characters",
    NUMERIC_CHARS: "Numeric Characters",
    SPECIAL_CHARS: "Special Characters",

    // Placeholders/Descriptions
    NAME_PLACEHOLDER: "Enter a name for the organization",
    DESCRIPTION_PLACEHOLDER: "Describe the organization",
    PROVIDER_PLACEHOLDER: "Select the parent organization of the current organization",
    AUTH_MECHANISM_DESC: "Select the method for authenticating users",
    PASSWORD_LENGTH_DESC: "Minimum number of characters in the password",
    UPPERCASE_CHARS_DESC: "Minimum number of uppercase characters in the password",
    LOWERCASE_CHARS_DESC: "Minimum number of lowercase characters in the password",
    NUMERIC_CHARS_DESC: "Minimum number of numeric (0-9) characters in the password",
    SPECIAL_CHARS_DESC: "Minimum number of special !@#$%^&*().,? characters in the password",

    // Distribution Center
    DIST_AREA_LABEL: "Create private and shared Distribution Areas",
    DIST_AREA_TITLE:
        "Create a distribution area on the Gluware Distribution Center for sharing custom packages. Only check this box if Gluware Support asks you to.",
    DIST_LAB_VC_LABEL: "Enable use of Git repositories in Solutions Manager",
    DIST_LAB_VC_TITLE: "Enable use of Git repositories in Solutions Manager",
    DIST_AREAS_CREATED: "Distribution areas have already been created.",

    // Authentication
    SSO_MESSAGE: "User authentication is managed with Single Sign-On.",

    // Buttons
    SAVE_BTN: "Save",
    CANCEL_BTN: "Cancel",
    DELETE_BTN: "Delete",
};

// Authentication Mechanism Values
const AUTH_MECHANISMS = {
    GLUWARE: {label: "Gluware", value: "gluware"},
    LDAP: {label: "LDAP", value: "ldap"},
    RADIUS: {label: "RADIUS", value: "radius"},
};
const ButtonControl = uiCore.Control.Action.Button;
const InputTextControl = uiCore.Control.Textfield.InputText;
const TextAreaControl = uiCore.Control.Textfield.Textarea;
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const SingleCombobox = uiCore.Control.OptionSelection.Single.ComboBox;
const InputRadioControl = uiCore.Control.OptionSelection.Single.InputRadio;
const NumberControl = uiCore.Control.Textfield.InputNumber;

const LDAPComponent = require("../ldapConfig/LdapConfigComponent");
const RADIUSComponent = require("../radiusConfig/RadiusConfigComponent");
const ErrorMapTranslation = require("../../../commoncomponents/ErrorMapTranslation").Organization;
const generateDefaultErrorMessage =
    require("../../../commoncomponents/ErrorMapTranslation").generateDefaultErrorMessage;

class EditOrgRenderer {
    constructor(model, retentionPolicyEntity, uiUtils) {
        this._model = model;
        this._retentionPolicyEntity = retentionPolicyEntity;
        this._uiUtils = uiUtils;
        this._logger = this.model._logger;
        this._isDirty = false;

        this._rootElem = document.createElement("edit-gl");
        this._rootElem.classList.add("screen");
        this._rootElem.component = this._model;

        this._name = new InputTextControl({darkTheme: true});
        this._description = new TextAreaControl({darkTheme: true});
        this._distAreaSelection = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: UI_TEXT.DIST_AREA_LABEL,
            title: UI_TEXT.DIST_AREA_TITLE,
        });
        this._distLabVCEnabled = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: UI_TEXT.DIST_LAB_VC_LABEL,
            title: UI_TEXT.DIST_LAB_VC_TITLE,
        });
        this._providerControl = new SingleCombobox({
            darkTheme: true,
            autoSelect: false,
            selectionUnselectable: true,
        });

        this._editFormElem = document.createElement("edit-form-gl");
        this._editFormElem.classList.add("screen-form");
        this._rootElem.appendChild(this._editFormElem);

        //Generic Form User Info Header
        this._orgInfoHeader = document.createElement("h3");
        this._orgInfoHeader.innerText = UI_TEXT.ORG_INFO_HEADER;
        this._editFormElem.appendChild(this._orgInfoHeader);
        this._editFormElem.appendChild(document.createElement("hr"));

        this._renderPair(UI_TEXT.NAME, UI_TEXT.NAME_PLACEHOLDER, this._name, this._editFormElem);
        this._renderPair(
            UI_TEXT.DESCRIPTION,
            UI_TEXT.DESCRIPTION_PLACEHOLDER,
            this._description,
            this._editFormElem
        );

        this._providerElem = document.createElement("provider-gl");
        this._renderPair(
            UI_TEXT.PROVIDER,
            UI_TEXT.PROVIDER_PLACEHOLDER,
            this._providerControl,
            this._providerElem
        );
        this._editFormElem.appendChild(this._providerElem);

        this._authMechRadio = new InputRadioControl({
            darkTheme: true,
            horizontal: true,
        });

        this._authMechRadio.setOptions([
            AUTH_MECHANISMS.GLUWARE,
            Object.assign({}, AUTH_MECHANISMS.LDAP, {
                disabled: this._model._samloAuthExists,
            }),
            Object.assign({}, AUTH_MECHANISMS.RADIUS, {
                disabled: this._model._samloAuthExists,
            }),
        ]);

        this._renderPair(
            UI_TEXT.AUTH_MECHANISM,
            UI_TEXT.AUTH_MECHANISM_DESC,
            this._authMechRadio,
            this._editFormElem
        );

        if (this._model._samloAuthExists) {
            const samloAuthLabel = document.createElement("span");
            samloAuthLabel.classList.add("auth-mech-unavailable");
            samloAuthLabel.innerText = UI_TEXT.SSO_MESSAGE;
            this._editFormElem.appendChild(samloAuthLabel);
        }

        this._ldapComponent = new LDAPComponent({
            orgInfo: this.model.selectedOrgInfo,
            userInfo: this.model.userInfo,
            credentialsList: this._model._credentialsList,
        });
        this._editFormElem.appendChild(this._ldapComponent.renderer.rootElement);

        this._radiusComponent = new RADIUSComponent({
            orgInfo: this.model.selectedOrgInfo,
            userInfo: this.model.userInfo,
            credentialsList: this._model._credentialsList,
        });
        this._editFormElem.appendChild(this._radiusComponent.renderer.rootElement);

        // Password Complexity
        this._orgPWContainer = document.createElement("div");
        this._orgPWContainer.id = "passwordComplexity";

        this._editFormElem.appendChild(document.createElement("hr"));
        this._orgPWComplexHeader = document.createElement("h4");
        this._orgPWComplexHeader.innerText = UI_TEXT.PASSWORD_COMPLEXITY_HEADER;
        this._orgPWContainer.appendChild(this._orgPWComplexHeader);
        this._orgPWComplexSubHeader = document.createElement("p");
        this._orgPWComplexSubHeader.innerText = UI_TEXT.PASSWORD_COMPLEXITY_SUBHEADER;
        this._orgPWContainer.appendChild(this._orgPWComplexSubHeader);

        this._minPasswordLength = new NumberControl({darkTheme: true, min: 1, max: 256});
        this._minUpperCaseChar = new NumberControl({darkTheme: true, min: 0, max: 256});
        this._minLowerCaseChar = new NumberControl({darkTheme: true, min: 0, max: 256});
        this._minNumericChar = new NumberControl({darkTheme: true, min: 0, max: 256});
        this._minSpecialChar = new NumberControl({darkTheme: true, min: 0, max: 256});

        this._renderPair(
            UI_TEXT.PASSWORD_LENGTH,
            UI_TEXT.PASSWORD_LENGTH_DESC,
            this._minPasswordLength,
            this._orgPWContainer
        );
        this._renderPair(
            UI_TEXT.UPPERCASE_CHARS,
            UI_TEXT.UPPERCASE_CHARS_DESC,
            this._minUpperCaseChar,
            this._orgPWContainer
        );
        this._renderPair(
            UI_TEXT.LOWERCASE_CHARS,
            UI_TEXT.LOWERCASE_CHARS_DESC,
            this._minLowerCaseChar,
            this._orgPWContainer
        );
        this._renderPair(
            UI_TEXT.NUMERIC_CHARS,
            UI_TEXT.NUMERIC_CHARS_DESC,
            this._minNumericChar,
            this._orgPWContainer
        );
        this._renderPair(
            UI_TEXT.SPECIAL_CHARS,
            UI_TEXT.SPECIAL_CHARS_DESC,
            this._minSpecialChar,
            this._orgPWContainer
        );

        this._editFormElem.appendChild(this._orgPWContainer);

        // Distribution center
        this._editFormElem.appendChild(document.createElement("hr"));

        this._renderLabel(
            UI_TEXT.DISTRIBUTION_CENTER_HEADER,
            "dist-center-section-label",
            this._editFormElem
        );

        if (this._model._isDCEnabled) {
            this._renderMono(this._distAreaSelection, this._editFormElem);
        }

        this._confirmedDist = document.createElement("p");
        this._confirmedDist.innerText = UI_TEXT.DIST_AREAS_CREATED;
        this._editFormElem.appendChild(this._confirmedDist);

        this._renderMono(this._distLabVCEnabled, this._editFormElem);

        // Form Buttons
        this._buttonGroupContainer = document.createElement("button-group-container");
        this._buttonGroupContainer.classList.add("button-group");
        this._saveButton = new ButtonControl({id: "save", label: UI_TEXT.SAVE_BTN, disabled: true});
        this._undoButton = new ButtonControl({
            id: "undo",
            label: UI_TEXT.CANCEL_BTN,
            disabled: true,
        });
        this._deleteButton = new ButtonControl({
            id: "delete",
            label: UI_TEXT.DELETE_BTN,
            class: "destructive",
        });
        this._buttonGroupContainer.appendChild(this._deleteButton.renderer.getRootElement());
        this._buttonGroupContainer.appendChild(this._undoButton.renderer.getRootElement());
        this._buttonGroupContainer.appendChild(this._saveButton.renderer.getRootElement());
        this._rootElem.appendChild(this._buttonGroupContainer);

        this._registerListeners();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        this._unregisterListeners();

        let promArr = [];
        promArr.push(
            this._name.shutdown(),
            this._description.shutdown(),
            this._distAreaSelection.shutdown(),
            this._distLabVCEnabled.shutdown(),
            this._providerControl.shutdown(),
            this._saveButton.shutdown(),
            this._undoButton.shutdown(),
            this._deleteButton.shutdown(),
            this._ldapComponent.shutdown(),
            this._radiusComponent.shutdown(),
            this._minPasswordLength.shutdown(),
            this._minUpperCaseChar.shutdown(),
            this._minLowerCaseChar.shutdown(),
            this._minNumericChar.shutdown(),
            this._minSpecialChar.shutdown()
        );

        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._orgInfoHeader.remove();
                this._orgInfoHeader = undefined;
                this._providerElem.remove();
                this._providerElem = undefined;
                if (this._confirmedDist) {
                    this._confirmedDist.remove();
                }
                this._confirmedDist = undefined;
                this._saveButton = undefined;
                this._undoButton = undefined;
                this._deleteButton = undefined;
                this._buttonGroupContainer.remove();
                this._buttonGroupContainer = undefined;
                this._editFormElem.remove();
                this._editFormElem = undefined;
                this._authMechRadio = undefined;
                this._ldapComponent = undefined;
                this._radiusComponent = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;

                return; //just needs to explitly return nothing
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "EditOrgRenderer._shutdown() Error shutting down EditOrgRenderer."
                );
            }
        );
    }

    _registerListeners() {
        this._model.on("Shutdown", (this._onShutdown = this._onShutdown.bind(this)));
        this._model.on("SaveModelUpdated", (this._makeDirty = this._makeDirty.bind(this)));
        this._model.on("SaveModelCleared", (this.render = this.render.bind(this)));
        this._model.on("SaveModelPristine", (this._makePristine = this._makePristine.bind(this)));
        this._model.on("SaveOrgError", (this._saveOrgError = this._saveOrgError.bind(this)));
        this._model.on("DeleteOrgError", (this._deleteOrgError = this._deleteOrgError.bind(this)));
        this._model.on("TestLdapError", (this._testLdapError = this._testLdapError.bind(this)));
        this._model.on(
            "TestRadiusError",
            (this._testRadiusError = this._testRadiusError.bind(this))
        );
        this._name.on("UserValue", (this._nameChange = this._nameChange.bind(this)));
        this._providerControl.on(
            "UserValue",
            (this._providerControlChange = this._providerControlChange.bind(this))
        );
        this._description.on(
            "UserValue",
            (this._descriptionChange = this._descriptionChange.bind(this))
        );
        this._distAreaSelection.on(
            "UserValue",
            (this._distAreaSelectionChange = this._distAreaSelectionChange.bind(this))
        );
        this._distLabVCEnabled.on(
            "UserValue",
            (this._distLabVCEnabledChange = this._distLabVCEnabledChange.bind(this))
        );
        this._saveButton.on(
            "UserClick",
            (this._callSaveSelectedOrg = this._callSaveSelectedOrg.bind(this))
        );
        this._undoButton.on("UserClick", (this._undoOrgChanges = this._undoOrgChanges.bind(this)));
        this._deleteButton.on(
            "UserClick",
            (this._callDeleteSelectedOrg = this._callDeleteSelectedOrg.bind(this))
        );
        this._authMechRadio.on(
            "UserValue",
            (this._authMechChange = this._authMechChange.bind(this))
        );
        this._ldapComponent.on(
            "ModelUpdated",
            (this._onLdapModelChanged = this._onLdapModelChanged.bind(this))
        );
        this._ldapComponent.on("testLDAP", (this._onTestLdap = this._onTestLdap.bind(this)));
        this._radiusComponent.on(
            "ModelUpdated",
            (this._onRadiusModelChanged = this._onRadiusModelChanged.bind(this))
        );
        this._radiusComponent.on(
            "testRADIUS",
            (this._onTestRadius = this._onTestRadius.bind(this))
        );
        this._model.on(
            "PasswordPolicyChanged",
            (this._updatePWForm = this._updatePWForm.bind(this))
        );

        //  PW min values

        this._minPasswordLength.on(
            "UserValue",
            (this._updatePasswordPolicy = this._updatePasswordPolicy.bind(this))
        );
        this._minUpperCaseChar.on(
            "UserValue",
            (this._updatePasswordPolicy = this._updatePasswordPolicy.bind(this))
        );
        this._minLowerCaseChar.on(
            "UserValue",
            (this._updatePasswordPolicy = this._updatePasswordPolicy.bind(this))
        );
        this._minNumericChar.on(
            "UserValue",
            (this._updatePasswordPolicy = this._updatePasswordPolicy.bind(this))
        );
        this._minSpecialChar.on(
            "UserValue",
            (this._updatePasswordPolicy = this._updatePasswordPolicy.bind(this))
        );
    }

    _unregisterListeners() {
        this._model
            .removeAllListeners("Shutdown")
            .removeAllListeners("SaveModelUpdated")
            .removeAllListeners("SaveModelCleared")
            .removeAllListeners("SaveModelPristine")
            .removeAllListeners("SaveOrgError")
            .removeAllListeners("TestLdapError")
            .removeAllListeners("TestRadiusError")
            .removeAllListeners("DeleteOrgError");
        this._authMechRadio.removeListener("UserValue", this._authMechChange);
        this._ldapComponent.removeListener("ModelUpdated", this._onLdapModelChanged);
        this._ldapComponent.removeListener("testLDAP", this._onTestLdap);
        this._radiusComponent.removeListener("ModelUpdated", this._onRadiusModelChanged);
        this._radiusComponent.removeListener("testRADIUS", this._onTestRadius);
        this._name.removeAllListeners("UserValue");
        this._description.removeAllListeners("UserValue");
        this._providerControl.removeAllListeners("UserValue");
        this._distAreaSelection.removeAllListeners("UserValue");
        this._distLabVCEnabled.removeAllListeners("UserValue");
        this._saveButton.removeAllListeners("UserClick");
        this._undoButton.removeAllListeners("UserClick");
        this._deleteButton.removeAllListeners("UserClick");
        this._minPasswordLength.removeAllListeners("UserValue");
        this._minUpperCaseChar.removeAllListeners("UserValue");
        this._minLowerCaseChar.removeAllListeners("UserValue");
        this._minNumericChar.removeAllListeners("UserValue");
        this._minSpecialChar.removeAllListeners("UserValue");
    }

    _nameChange(val) {
        this.model.updateOrgSaveModelField("name", val);
    }

    _descriptionChange(val) {
        this.model.updateOrgSaveModelField("description", val);
    }

    _distAreaSelectionChange(val) {
        this.model.updateOrgSaveModelField("labEnabled", val, true);
    }

    _distLabVCEnabledChange(val) {
        this.model.updateOrgSaveModelField("labVersionControlEnabled", val, true);
    }

    _providerControlChange(val) {
        this.model.updateOrgSaveModelField("providerId", val);
    }

    _callSaveSelectedOrg() {
        //check, if we change the current provider, and show confirmation message, depends on the policy attached or not
        if (
            this._model._saveModel.providerId &&
            this._model._saveModel.providerId !== this._model._selectedOrgInfo.providerId
        ) {
            this._retentionPolicyEntity
                .retrieveRetentionPolicy(this._model._selectedOrgInfo.id)
                .then((retentionPolicy) => {
                    const confMessage = retentionPolicy.isAttached()
                        ? "The Data Retention Policy and shared Custom Field definitions may differ from the current Provider. Do you wish to continue?"
                        : "Shared Custom Field definitions may differ from the current Provider. Do you wish to continue?";
                    this._uiUtils
                        .showConfirmDialog({
                            message: confMessage,
                            confirmButtonName: "Confirm",
                            cancelButtonName: "Cancel",
                        })
                        .then(
                            () => {
                                this._saveSelectedOrg();
                            },
                            () => {
                                //cancel
                            }
                        );
                })
                .catch((error) => {
                    this._logger.error("Error saving org:", error);
                });
        } else {
            //otherwise just save the current org
            this._saveSelectedOrg();
        }
    }

    _updatePasswordPolicy() {
        const formValues = {
            minPasswordLength: this._minPasswordLength.value,
            minUpperCaseChar: this._minUpperCaseChar.value,
            minLowerCaseChar: this._minLowerCaseChar.value,
            minNumericChar: this._minNumericChar.value,
            minSpecialChar: this._minSpecialChar.value,
        };

        this.model.updateOrgPWSaveModelField(formValues);
    }

    _updatePWForm() {
        this._minPasswordLength.value = this._model._passwordPolicy.minPasswordLength;
        this._minUpperCaseChar.value = this._model._passwordPolicy.minUpperCaseChar;
        this._minLowerCaseChar.value = this._model._passwordPolicy.minLowerCaseChar;
        this._minNumericChar.value = this._model._passwordPolicy.minNumericChar;
        this._minSpecialChar.value = this._model._passwordPolicy.minSpecialChar;
    }

    _saveSelectedOrg() {
        this._disableButtons();
        this.model.saveSelectedOrg();
    }

    _testLdapError(err) {
        this._uiUtils.clearMessage();
        let appErr = err;
        switch (appErr.errorCode) {
            case "ValidationError":
                if (_.isPlainObject(appErr.errorDetails)) {
                    const errorNames = Object.keys(appErr.errorDetails);
                    if (errorNames && errorNames.length > 1) {
                        this._uiUtils.writeErrorMessage("Save Errors:");
                    } else {
                        this._uiUtils.writeErrorMessage("Save Error:");
                    }
                    errorNames.forEach((propNameErr, index) => {
                        let propNamePath = propNameErr.replace(/,/, "."),
                            invalidText = generateDefaultErrorMessage(propNamePath);
                        if (_.get(ErrorMapTranslation, propNamePath, false)) {
                            invalidText = _.get(
                                ErrorMapTranslation,
                                propNamePath
                            ).toLocaleLowerCase();
                        }
                        if (index === errorNames.length - 2) {
                            invalidText = invalidText + " and";
                        } else if (index !== errorNames.length - 1) {
                            invalidText = invalidText + ",";
                        }
                        this._uiUtils.writeErrorMessage(invalidText);
                        this._logger.warn(
                            {error: appErr},
                            "EditOrgRenderer._testLdapError() Error: " + invalidText
                        );
                    });
                }
                break;
            case "CreateError":
                if (appErr.code === 11000) {
                    let authMech = this._authMechRadio.value.toUpperCase();
                    this._uiUtils.showMessageDialog({
                        message: `Error: ${authMech} domain is a duplicate of an existing authentication domain`,
                        confirmButtonName: "OK",
                    });
                } else {
                    this._uiUtils.writeErrorMessage(appErr.errorMsg || appErr.message);
                }
                break;
            default:
                this._uiUtils.writeErrorMessage(appErr.errorMsg || appErr.message);

                break;
        }
    }

    _testRadiusError(err) {
        this._uiUtils.clearMessage();
        let appErr = err;
        switch (appErr.errorCode) {
            case "ValidationError":
                if (_.isPlainObject(appErr.errorDetails)) {
                    const errorNames = Object.keys(appErr.errorDetails);
                    if (errorNames && errorNames.length > 1) {
                        this._uiUtils.writeErrorMessage("Save Errors:");
                    } else {
                        this._uiUtils.writeErrorMessage("Save Error:");
                    }
                    errorNames.forEach((propNameErr, index) => {
                        let propNamePath = propNameErr.replace(/,/, "."),
                            invalidText = generateDefaultErrorMessage(propNamePath);
                        if (_.get(ErrorMapTranslation, propNamePath, false)) {
                            invalidText = _.get(
                                ErrorMapTranslation,
                                propNamePath
                            ).toLocaleLowerCase();
                        }
                        if (index === errorNames.length - 2) {
                            invalidText = invalidText + " and";
                        } else if (index !== errorNames.length - 1) {
                            invalidText = invalidText + ",";
                        }
                        this._uiUtils.writeErrorMessage(invalidText);
                        this._logger.warn(
                            {error: appErr},
                            "EditOrgRenderer._testRadiusError() Error: " + invalidText
                        );
                    });
                }
                break;
            default:
                this._uiUtils.writeErrorMessage(appErr.errorMsg || appErr.message);
                break;
        }
    }

    _saveOrgError(err) {
        this._uiUtils.clearMessage();
        let appErr = err;
        switch (appErr.errorCode) {
            case "ValidationError":
                if (_.isPlainObject(appErr.errorDetails)) {
                    const errorNames = Object.keys(appErr.errorDetails);
                    if (errorNames && errorNames.length > 1) {
                        this._uiUtils.writeErrorMessage("Save Errors:");
                    } else {
                        this._uiUtils.writeErrorMessage("Save Error:");
                    }
                    errorNames.forEach((propNameErr, index) => {
                        let propNamePath = propNameErr.replace(/,/, "."),
                            invalidText = generateDefaultErrorMessage(propNamePath);
                        if (_.get(ErrorMapTranslation, propNamePath, false)) {
                            invalidText = _.get(
                                ErrorMapTranslation,
                                propNamePath
                            ).toLocaleLowerCase();
                        }
                        if (index === errorNames.length - 2) {
                            invalidText = invalidText + " and";
                        } else if (index !== errorNames.length - 1) {
                            invalidText = invalidText + ",";
                        }
                        this._uiUtils.writeErrorMessage(invalidText);
                    });
                }
                break;
            case "NotAuthorized":
                if (appErr.errorMsg) {
                    this._uiUtils.showMessageDialog({
                        message: "An error occurred: " + this._uiUtils.errorCodeMapping(appErr),
                        confirmButtonName: "OK",
                    });
                }
                break;
            case "CreateError":
                if (appErr.code === 11000) {
                    let authMech = this._authMechRadio.value.toUpperCase();
                    this._uiUtils.showMessageDialog({
                        message: `Error: ${authMech} domain is a duplicate of an existing authentication domain`,
                        confirmButtonName: "OK",
                    });
                } else {
                    this._uiUtils.writeErrorMessage(appErr.errorMsg || appErr.message);
                }
                break;
            case "DistributionCenterServiceError":
                appErr.errorMsg = "Unable to access the Gluware Distribution Center";
                this._uiUtils.writeErrorMessage(appErr.errorMsg || appErr.message);
                this._distAreaSelection.setValue(false);
                this._distAreaSelection.emit("UserValue", false);
                break;
            case "PasswordPolicyValidation":
                this._uiUtils.writeErrorMessage(appErr.errorMsg || appErr.message);
                this._saveButton.setDisabled(true);
                this._undoButton.setDisabled(false);
                break;
            default:
                this._uiUtils.writeErrorMessage(appErr.errorMsg || appErr.message);
                break;
        }
        if (
            appErr.errorCode !== "DistributionCenterServiceError" &&
            appErr.errorCode !== "PasswordPolicyValidation"
        ) {
            this._saveButton.setDisabled(false);
            this._undoButton.setDisabled(false);
        }
    }

    _undoOrgChanges() {
        this._disableButtons();
        this._uiUtils.clearMessage();
        this.model.clearOrgSaveModel();
    }

    _callDeleteSelectedOrg() {
        this.model.deleteSelectedOrg();
    }

    _deleteOrgError(err) {
        this._uiUtils.writeErrorMessage("Delete Error: " + this._uiUtils.errorCodeMapping(err));
        this._deleteButton.setDisabled(false);
        //this._deleteButton.setDestructive(true);  //no longer used
    }

    _renderLabel(labelName, labelClass, container) {
        const labelElem = document.createElement("label");
        labelElem.innerText = labelName;
        labelElem.classList.add("form-label-gl");
        if (labelClass) {
            labelElem.classList.add(labelClass);
        }
        container.appendChild(labelElem);
    }

    _renderPair(labelName, labelTitle, component, container, labelClass) {
        let labelElem = document.createElement("label");
        labelElem.innerText = labelName;
        labelElem.title = labelTitle;
        labelElem.classList.add("form-label-gl");
        if (labelClass) {
            labelElem.classList.add(labelClass);
        }
        let cmpntElem = component.renderer.getRootElement();

        container.appendChild(labelElem);
        container.appendChild(cmpntElem);
    }

    _renderMono(component, container) {
        let cmpntElem = component.renderer.getRootElement();
        container.appendChild(cmpntElem);
    }

    _disableButtons() {
        this._saveButton.setDisabled(true);
        this._undoButton.setDisabled(true);
        this._deleteButton.setDisabled(true);
        // this._deleteButton.setDestructive(false);
    }

    _makeDirty(keepMessage = false) {
        // if (!this._isDirty){
        this._saveButton.setDisabled(false);
        this._undoButton.setDisabled(false);
        this._deleteButton.setDisabled(true);
        // this._deleteButton.setDestructive(false);
        if (!keepMessage) {
            this._uiUtils.clearMessage();
        }
        this._isDirty = true;
        // }
    }

    _makePristine() {
        // if (this._isDirty){
        this._saveButton.setDisabled(true);
        this._undoButton.setDisabled(true);
        this._deleteButton.setDisabled(false);
        // this._deleteButton.setDestructive(true);
        this._resetPWPolicy();
        this._isDirty = false;
        // }
    }

    _resetPWPolicy() {
        this._minPasswordLength.value = this.model._oldPWPolicy.minPasswordLength;
        this._minUpperCaseChar.value = this.model._oldPWPolicy.minUpperCaseChar;
        this._minLowerCaseChar.value = this.model._oldPWPolicy.minLowerCaseChar;
        this._minNumericChar.value = this.model._oldPWPolicy.minNumericChar;
        this._minSpecialChar.value = this.model._oldPWPolicy.minSpecialChar;
    }

    _renderCreateDistributionAreas() {
        if (this.model.selectedOrgInfo.labEnabled) {
            this._distAreaSelection.renderer.getRootElement().style.display = "none";
            if (this._confirmedDist) {
                this._confirmedDist.style.display = "";
            }
        } else {
            this._distAreaSelection.renderer.getRootElement().style.display = "";
            if (this._confirmedDist) {
                this._confirmedDist.style.display = "none";
            }
        }
    }

    _renderProviderList() {
        return new Promise((resolve) => {
            if (this.model.selectedOrgInfo.eligibleProviders.length) {
                let selectedOrg = this._model.selectedOrgInfo;
                let toBeSelectedOrg;
                let provOpts = [];
                this.model.selectedOrgInfo.eligibleProviders.forEach((org) => {
                    let option = {
                        label: org.name,
                        value: org.id,
                    };
                    provOpts.push(option);
                    if (selectedOrg.providerId === org.id) {
                        toBeSelectedOrg = option;
                    }
                });
                provOpts = _.sortBy(provOpts, [
                    function (option) {
                        return option.label.toLowerCase();
                    },
                ]);
                this._providerControl.options = provOpts;
                this._providerControl.value = toBeSelectedOrg
                    ? toBeSelectedOrg.value
                    : provOpts[0].value;
                this._providerElem.classList.remove("hidden");
            } else {
                this._providerControl.options = [];
                this._providerControl.value = undefined;
                this._providerElem.classList.add("hidden");
            }
            resolve();
        });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        if (this.model.selectedOrgInfo) {
            this._name.value = this.model.selectedOrgInfo.name;
            this._description.value = this.model.selectedOrgInfo.description;

            this._renderProviderList();
            //Set before setSelectedOrgInfo - that call renders the child, which reads this list.
            this._ldapComponent.setCredentialsList(this._model._credentialsList);
            this._radiusComponent.setCredentialsList(this._model._credentialsList);
            this._ldapComponent.setSelectedOrgInfo(this.model.selectedOrgInfo);
            this._radiusComponent.setSelectedOrgInfo(this.model.selectedOrgInfo);

            this._distLabVCEnabled.value =
                this.model.selectedOrgInfo.labVersionControlEnabled || false;

            this._authMechRadio.setValue(this.model.selectedOrgInfo.authMethod);
            // TODO To be improved -- Delay to allow Ldap and Radius component to retrieve action groups
            setTimeout(() => {
                this._authMechChange(this.model.selectedOrgInfo.authMethod);
            }, 500);

            this._renderCreateDistributionAreas();
        } else {
            console.error("Failed to retrieve selected organization");
        }
        this._makePristine();
        return Promise.resolve();
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onShutdown() {
        this._shutdown();
    }

    _authMechChange(authMech) {
        this.model.updateOrgSaveModelField("authMethod", authMech);
        switch (authMech) {
            case "ldap":
                this._radiusComponent.setEnabled(false);
                this._ldapComponent.setEnabled(true);
                break;
            case "radius":
                this._ldapComponent.setEnabled(false);
                this._radiusComponent.setEnabled(true);
                break;
            default: // gluware and any other options
                this._ldapComponent.setEnabled(false);
                this._radiusComponent.setEnabled(false);
        }
    }

    _onLdapModelChanged() {
        let ldapConfig = this._ldapComponent.getSaveModel();
        if (ldapConfig) {
            this.model.updateOrgSaveModelField("ldap", ldapConfig);
        } else {
            this.model.removeOrgSaveModelField("ldap");
        }
    }

    _onTestLdap(data) {
        this.model.emitTestLdap(data);
    }

    _onRadiusModelChanged() {
        let radiusConfig = this._radiusComponent.getSaveModel();
        if (radiusConfig) {
            this.model.updateOrgSaveModelField("radius", radiusConfig);
        } else {
            this.model.removeOrgSaveModelField("radius");
        }
    }

    _onTestRadius(data) {
        this.model.emitTestRadius(data);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/
    //TODO - Nobody should be using methods on a Renderer directly so make all these methods private
    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}
module.exports = EditOrgRenderer;
